// PanelGlass.js
// Glass / Door Lite section for Steel Doors.
// Visible only when the selected door panel has doorlite=1.
// Follows the data/onChange pattern used by other panel sections.

import React, { useState, useEffect, useRef } from 'react';
import ExpandableSection from '../../../expandablesection';

import DoorLiteSearch     from '../../controls/doorlite/DoorLiteSearch';
import DoorLiteCollection from '../../controls/doorlite/DoorLiteCollection';
import DoorLiteSize       from '../../controls/doorlite/DoorLiteSize';
import DoorLiteLites      from '../../controls/doorlite/DoorLiteLites';
import DoorLiteLocation   from '../../controls/doorlite/DoorLiteLocation';
import DoorLiteFrame      from '../../controls/doorlite/DoorLiteFrame';
import DoorLiteItems      from '../../controls/doorlite/DoorLiteItems';

const ev = '';

const PanelGlass = ({
    data          = {},
    onChange,
    disabled      = false,
    sectionId     = 'Glass',
    state,
    onExpandChange,
    forceExpanded,
    selectedPanel = null,   // the door panel object from PanelDoorpanel
}) => {

    const [searchText,  setSearchText]  = useState(data.searchText  || ev);
    const [collection,  setCollection]  = useState(data.collection  || ev);
    const [liteSize,    setLiteSize]    = useState(data.liteSize    || null);  // full size object
    const [lites,       setLites]       = useState(data.lites       || ev);
    const [location,    setLocation]    = useState(data.location    || ev);
    const [frame,       setFrame]       = useState(data.frame       || ev);
    const [selectedLite,setSelectedLite]= useState(data.selectedLite|| null);

    // ── sync from saved data (once) ───────────────────────────────────────────
    const loadedRef = useRef(false);
    useEffect(() => {
        if (loadedRef.current) return;
        if (!data || Object.keys(data).length === 0) return;
        loadedRef.current = true;
        setSearchText(data.searchText   || ev);
        setCollection(data.collection   || ev);
        setLiteSize(data.liteSize       || null);
        setLites(data.lites             || ev);
        setLocation(data.location       || ev);
        setFrame(data.frame             || ev);
        setSelectedLite(data.selectedLite || null);
    }, [data]);

    // ── bubble up ─────────────────────────────────────────────────────────────
    const isMounted = useRef(false);
    useEffect(() => {
        if (!isMounted.current) { isMounted.current = true; return; }
        onChange?.({ searchText, collection, liteSize, lites, location, frame, selectedLite });
    }, [searchText, collection, liteSize, lites, location, frame, selectedLite]);

    const title = selectedLite?.value
        ? `🪟 Glass / Door Lite  (${selectedLite.value})`
        : '🪟 Glass / Door Lite';

    // Flags derived from the selected size row
    const numberOfLites = liteSize?.lites    || 0;
    const showLocation  = liteSize?.location === 1;
    const showFrame     = liteSize?.frame    === 1;

    // controlMap — DoorLiteItems is rendered as ExpandableSection child (stable)
    const controlMap = [
        {
            id: 'DoorLiteSearch',
            component: (
                <DoorLiteSearch
                    placeholder="Search..."
                    onChange={setSearchText}
                />
            ),
            position: [1, 1],
            status: 'active',
            expandableSectionId: sectionId,
        },
        {
            id: 'DoorLiteCollection',
            component: (
                <DoorLiteCollection
                    value={collection}
                    onChange={setCollection}
                    disabled={disabled}
                />
            ),
            position: [2, 1],
            status: 'active',
            expandableSectionId: sectionId,
        },
        {
            id: 'DoorLiteSize',
            component: (
                <DoorLiteSize
                    value={liteSize}
                    onChange={setLiteSize}
                    selectedPanel={selectedPanel}
                    disabled={disabled}
                />
            ),
            position: [2, 2],
            status: 'active',
            expandableSectionId: sectionId,
        },
        ...(numberOfLites > 1 ? [{
            id: 'DoorLiteLites',
            component: (
                <DoorLiteLites
                    value={lites}
                    onChange={setLites}
                    numberOfLites={numberOfLites}
                    disabled={disabled}
                />
            ),
            position: [2, 3],
            status: 'active',
            expandableSectionId: sectionId,
        }] : []),
        ...(showLocation ? [{
            id: 'DoorLiteLocation',
            component: (
                <DoorLiteLocation
                    value={location}
                    onChange={setLocation}
                    isVisible={1}
                    disabled={disabled}
                />
            ),
            position: [3, 1],
            status: 'active',
            expandableSectionId: sectionId,
        }] : []),
        ...(showFrame ? [{
            id: 'DoorLiteFrame',
            component: (
                <DoorLiteFrame
                    value={frame}
                    onChange={setFrame}
                    isVisible={1}
                    disabled={disabled}
                />
            ),
            position: [3, 2],
            status: 'active',
            expandableSectionId: sectionId,
        }] : []),
    ];

    return (
        <ExpandableSection
            title={title}
            sectionId={sectionId}
            state={state}
            onExpandChange={onExpandChange}
            forceExpanded={forceExpanded}
            panelLocation="Glass"
            controlMap={controlMap.filter((c) => c.expandableSectionId === sectionId)}
        >
            {/* DoorLiteItems rendered outside controlMap to avoid remount on every render */}
            <DoorLiteItems
                value={selectedLite}
                onChange={setSelectedLite}
                selectedCollection={collection}
                selectedSize={liteSize?.value || ''}
                searchByName={searchText}
            />
        </ExpandableSection>
    );
};

export default PanelGlass;
